import { useState } from 'react';
import { Check, Loader2, Search, UserCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { poDraftImportApi, type PoCustomer, type PoReview } from '@/lib/po-draft-import-api';
import { useSearch } from './use-search';

interface Props {
  buyer: PoReview['extracted']['buyer'];
  candidates: PoCustomer[];
  storeId: string; token: string;
  selected: PoCustomer | null; confirmed: boolean;
  onSelect: (c: PoCustomer | null) => void; onConfirm: () => void; disabled: boolean;
  savedCustomer?: PoCustomer | null; savedStatus?: 'available' | 'unavailable' | 'ambiguous';
}

function Row({ c, active, onPick, disabled, saved }: { c: PoCustomer; active: boolean; onPick: () => void; disabled: boolean; saved?: boolean }) {
  return (
    <button type="button" disabled={disabled} onClick={onPick} data-testid={`button-customer-${c.id.split('/').pop()}`}
      className={`w-full text-left px-3 py-2 rounded border text-[13px] flex items-start gap-2 transition-colors ${active ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'}`}>
      <span className="flex-1 min-w-0">
        <span className="block font-medium truncate">{saved && <span className="mr-1.5 rounded bg-primary/15 text-primary px-1.5 py-0.5 text-[10px] font-semibold uppercase" data-testid="badge-saved-customer">Saved customer</span>}{c.name || c.email || 'Unnamed customer'}</span>
        <span className="block text-xs text-muted-foreground truncate">{[c.company, c.email, c.phone].filter(Boolean).join(' · ')}</span>
      </span>
      {active && <Check className="w-4 h-4 text-primary mt-0.5" />}
    </button>
  );
}

export function CustomerPicker({ buyer, candidates, storeId, token, selected, confirmed, onSelect, onConfirm, disabled, savedCustomer, savedStatus }: Props) {
  const [q, setQ] = useState('');
  const { results, loading, error } = useSearch<PoCustomer>((s, sig) => poDraftImportApi.customers(storeId, s, token, sig), q, storeId);
  return (
    <section className="glass-panel p-4 space-y-3" data-testid="section-customer">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Billing customer (Shopify)</h3>
        {selected && confirmed && <span className="text-xs text-primary flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" />Confirmed</span>}
      </div>
      <div className="rounded bg-muted/50 px-3 py-2 text-xs text-muted-foreground" data-testid="text-extracted-buyer">
        <div className="font-medium text-foreground text-[13px]">Buyer on PDF</div>
        {[buyer.company, buyer.name, buyer.email, buyer.phone, buyer.accountNumber && `Acct ${buyer.accountNumber}`].filter(Boolean).join(' · ') || 'No buyer details extracted'}
      </div>
      {selected ? (
        <div className="space-y-2">
          <Row c={selected} active disabled={disabled} onPick={() => {}} />
          <div className="flex gap-2">
            {!confirmed && <Button size="sm" disabled={disabled} onClick={onConfirm} data-testid="button-confirm-customer">Confirm this customer</Button>}
            <Button size="sm" variant="outline" disabled={disabled} onClick={() => onSelect(null)} data-testid="button-change-customer">Change</Button>
          </div>
          {!confirmed && <p className="text-xs text-amber-600">Check this is the right account before confirming.</p>}
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {candidates.length ? 'Possible matches. None are assumed correct; select one.' : 'No candidates found. Search for the customer below.'}
          </p>
          {savedStatus && (
            <div className="text-xs rounded border border-primary/30 bg-primary/5 px-2 py-1.5" data-testid="text-saved-customer-notice">
              {savedStatus === 'available' && 'A saved customer exists for this buyer. It is a suggestion only; select and confirm it yourself.'}
              {savedStatus === 'unavailable' && 'The saved customer for this buyer is no longer available in Shopify. Search for the right customer.'}
              {savedStatus === 'ambiguous' && 'More than one saved customer applies to this buyer. Choose the right one.'}
            </div>
          )}
          {savedStatus === 'available' && savedCustomer && <Row c={savedCustomer} saved active={false} disabled={disabled} onPick={() => onSelect(savedCustomer)} />}
          <div className="space-y-1.5">{candidates.filter((c) => c.id !== savedCustomer?.id || savedStatus !== 'available').map((c) => <Row key={c.id} c={c} active={false} disabled={disabled} onPick={() => onSelect(c)} />)}</div>
        </>
      )}
      {!selected && (
        <div className="space-y-2 pt-1 border-t border-border">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
            <Input className="pl-8" placeholder="Search by name, company, email or phone" value={q} disabled={disabled} onChange={(e) => setQ(e.target.value)} data-testid="input-customer-search" />
          </div>
          {loading && <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Searching…</div>}
          {error && <div className="text-xs text-destructive">{error}</div>}
          {!loading && !error && q.trim().length >= 2 && results.length === 0 && <div className="text-xs text-muted-foreground">No customers found.</div>}
          <div className="space-y-1.5 max-h-56 overflow-y-auto">{results.map((c) => <Row key={c.id} c={c} active={false} disabled={disabled} onPick={() => onSelect(c)} />)}</div>
        </div>
      )}
    </section>
  );
}
